import { Database, Warning, Info, Code, Link as LinkIcon, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

const CodeBlock = ({ children, testId }) => (
  <pre data-testid={testId} className="bg-[#0B1220] text-[#D1D5DB] text-xs leading-relaxed rounded-lg p-4 overflow-x-auto font-data whitespace-pre">
    {children}
  </pre>
);

const Section = ({ number, title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-8 h-8 rounded-full bg-[#004B87] text-white font-heading font-bold text-sm flex items-center justify-center shrink-0">
        {number}
      </div>
      <h2 className="font-heading text-lg font-bold text-[#101828]">{title}</h2>
    </div>
    <div className="pl-11 text-sm text-[#475569] leading-relaxed space-y-3">{children}</div>
  </section>
);

const FileCard = ({ name, format, purpose, verdict, testId }) => (
  <div className="border border-[#EAECF0] rounded-lg p-4 bg-white min-w-0" data-testid={testId}>
    <p className="font-data text-xs font-bold text-[#004B87] break-all">{name}</p>
    <p className="text-xs text-[#667085] mt-1"><strong>Format:</strong> {format}</p>
    <p className="text-sm text-[#475569] mt-2">{purpose}</p>
    <p className="text-xs mt-2 font-semibold" style={{ color: verdict.color }}>{verdict.text}</p>
  </div>
);

const RoleRow = ({ code, label, verified, note }) => (
  <tr className="border-b border-[#EAECF0] last:border-0">
    <td className="py-2 pr-4 font-data text-xs text-[#101828] font-bold">{code}</td>
    <td className="py-2 pr-4 text-xs text-[#475569]">{label}</td>
    <td className="py-2 pr-4 text-xs">
      {verified === true && <span className="text-[#067647] inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Confirmed via live data</span>}
      {verified === false && <span className="text-[#B42318] inline-flex items-center gap-1"><XCircle size={14} weight="fill" /> Ruled out</span>}
      {verified === "partial" && <span className="text-[#B54708] inline-flex items-center gap-1"><Warning size={14} weight="fill" /> Found but unreliable</span>}
    </td>
    <td className="py-2 text-xs text-[#667085]">{note}</td>
  </tr>
);

export default function SapInboundDeliveryInvestigationDocsPage() {
  return (
    <div className="min-h-screen bg-[#F9FAFB] overflow-x-hidden" data-testid="sap-inbound-delivery-investigation-docs-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center px-4 sm:px-6 shrink-0 z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-white/15 flex items-center justify-center">
            <Database size={18} className="text-white" weight="fill" />
          </div>
          <span className="font-heading text-[16px] font-bold text-white tracking-tight">SAP Integration Reference &middot; Inbound Delivery Investigation</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-10">
          <p className="font-data text-xs font-bold text-[#004B87] uppercase tracking-widest mb-2" data-testid="doc-code">SOP-SAP-ID-01 &middot; Inbound Delivery data model + GRN double-post incident</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] tracking-tight" data-testid="page-title">
            Inbound Delivery investigation: Notification vs Fulfilled Qty, and the S001330 double-post
          </h1>
          <p className="text-base text-[#475569] mt-3 max-w-2xl leading-relaxed">
            Full record of two related investigations: (1) hunting for SAP's "Fulfilled Quantity" field across
            every OData service and metadata export we could reach, and (2) the exact root cause and timeline of
            shipment S001330 being posted to SAP twice. Kept as a durable reference so the next person doesn't
            re-walk the same dead ends.
          </p>
        </div>

        <Section number={1} title="The 4 source files examined" testId="section-files">
          <div className="grid sm:grid-cols-2 gap-3 min-w-0">
            <FileCard
              testId="file-khconfirmedinbounddelivery"
              name="khconfirmedinbounddelivery.xml"
              format="Standard OData $metadata (CSDL/EDMX)"
              purpose="The custom OData service this app already calls in production (sap_confirmed_inbound_delivery_client.py). Exposes ConfirmedInboundDeliveryCollection -> Item -> DeliveryQuantity, PurchaseOrderReference."
              verdict={{ text: "DeliveryQuantity confirmed = Delivery Notification Qty (not Fulfilled)", color: "#B54708" }}
            />
            <FileCard
              testId="file-inboundemergentodata"
              name="inboundemergentodata.xml"
              format="Standard OData $metadata (CSDL/EDMX)"
              purpose="A newer custom service (name suggests purpose-built after our earlier question). Exposes InboundDeliveryCollection, ConfirmedInboundDeliveryCollection, and a standalone InboundDeliveryRequestConfirmationItemCollection with QuantityRoleCode-tagged rows."
              verdict={{ text: "Delivery Notification Qty confirmed reliable; Fulfilled Qty not reproducible", color: "#B54708" }}
            />
            <FileCard
              testId="file-khinbounddelivery"
              name="khinbounddelivery.xml"
              format="SAP Service Builder export (ABAP asx:abap, base64-encoded inner SADL definition) - not a plain $metadata document"
              purpose="The SOURCE DEFINITION of a third, older service, created 2026-09-22. Decoding the base64 SERVICE_DATA_BINARY/CONTENT reveals its ItemQuantity node binds to ABAP BO path APDL_INBOUND_DELIVERY~ITEM_QUANTITY (the unconfirmed Notification side), plus a PostGoodsReceipt OData ACTION mapped to the native ABAP action APDL_INBOUND_DELIVERY.PGR_BACKGROUND."
              verdict={{ text: "Confirms role-code scheme is standard across all 3 services; no new Fulfilled Qty field found", color: "#B54708" }}
            />
            <FileCard
              testId="file-bo-doc-html"
              name="Business Object Documentation InboundDelivery.html"
              format="SAP's own rendered BO documentation page (HTML/CSS, mostly standard GDT type descriptions)"
              purpose="Checked for a plain-English definition of 'Fulfilled Quantity', 'RequestConfirmationItem', or quantity role codes 17/18/23/24/139/140."
              verdict={{ text: "No definition found for any of these terms in this document", color: "#B42318" }}
            />
          </div>
        </Section>

        <Section number={2} title="What we were trying to answer" testId="section-goal">
          <p>
            SAP's native "Inbound Delivery" screen (<em>Details &rarr; Line Items</em> tab) shows two side-by-side
            columns per PO item: <strong>Delivery Notification Quantity</strong> (what this app told SAP it shipped)
            and <strong>Fulfilled Quantity</strong> (what SAP shows as actually processed). The ask: expose the same
            comparison as an admin-only flag on the Confirmed GRNs page, mirroring the existing
            "Over-Received in SAP" flag.
          </p>
          <p>
            Live example used throughout (delivery <strong>53935</strong>, PO <strong>29672</strong>): item 2 showed
            Notification Qty <strong>8,000</strong> ea vs Fulfilled Qty <strong>4,000</strong> ea; item 3 showed
            <strong> 4,000</strong> vs <strong>4,000</strong> (no variance). A second real example was supplied later
            (delivery 55745, PO 29976: Notification <strong>1,100</strong> kg vs Fulfilled <strong>1,000</strong> kg,
            status "In Process"/"Not Released").
          </p>
        </Section>

        <Section number={3} title="Delivery Notification Quantity - confirmed, reliable, 3 independent confirmations" testId="section-notification-qty">
          <p>All three sources agree, and all three were tested live against delivery 53935/item 2 and returned exactly 8,000:</p>
          <CodeBlock testId="notification-qty-proof">{`GET .../khconfirmedinbounddelivery/ConfirmedInboundDeliveryCollection
    ?$filter=ID eq '53935'&$expand=Item,Item/DeliveryQuantity,Item/PurchaseOrderReference
-> Item[ID=2].DeliveryQuantity.Quantity = 8000.00000000000000   (PO ref: 29672 item 2)

GET .../inboundemergentodata/ConfirmedInboundDeliveryItemQuantityCollection
    ?$filter=ParentObjectID eq 'FA163E4794691FE1ADC91ED420D21100'
-> role 18 "Delivery Quantity"               = 8000
-> role 140 "Qty listed in a delivery note"  = 8000

GET .../inboundemergentodata/ConfirmedInboundDeliveryDeliveryQuantityCollection
    ?$filter=ParentObjectID eq 'FA163E4794691FE1ADC91ED420D21100'
-> role 18 = 8000, role 140 = 8000  (same item, different nav path, same answer)`}</CodeBlock>
          <p>
            This is what the app's existing <code className="bg-[#F2F4F7] px-1 rounded text-xs">sap_confirmed_inbound_delivery_client.find_confirmation_rows()</code> already
            returns as <code className="bg-[#F2F4F7] px-1 rounded text-xs">quantity</code> - no new SAP client was needed for this half.
          </p>
        </Section>

        <Section number={4} title="Fulfilled Quantity - not found as a queryable field, despite 25+ live probes" testId="section-fulfilled-qty">
          <p>Every entity/role-code combination that looked promising, tested against real tenant data:</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse mt-2">
              <thead>
                <tr className="border-b border-[#EAECF0] text-left">
                  <th className="py-2 pr-4 text-xs font-bold text-[#101828]">Role/Field</th>
                  <th className="py-2 pr-4 text-xs font-bold text-[#101828]">SAP label (from decoded codelist)</th>
                  <th className="py-2 pr-4 text-xs font-bold text-[#101828]">Result</th>
                  <th className="text-xs font-bold text-[#101828]">Why ruled out</th>
                </tr>
              </thead>
              <tbody>
                <RoleRow code="khconfirmedinbounddelivery: Item.DeliveryQuantity" label="(unlabeled, single field)" verified={false} note="Returns 8000, i.e. the Notification Qty again, not 4000" />
                <RoleRow code="18 / 140" label="Delivery Quantity / Qty listed in a delivery note" verified={false} note="Both always equal the Notification Qty" />
                <RoleRow code="24" label="Fulfilled Quantity" verified="partial" note="Exists on a DIFFERENT, unrelated entity (InboundDeliveryRequestConfirmationItemQuantity) with its own ItemUUID key-space - 5 different UUID candidates tried (ConfirmedInboundDeliveryItem/Item1 ObjectID, InboundDeliveryItem1 UUID via notification ID), none matched" />
                <RoleRow code="23 / 17" label="Forwarded Quantity / Delivered Quantity" verified="partial" note="Scanned 4,000+ live rows: 23 always equals 17; neither ever showed the 8000-vs-4000 shortfall pattern from the screenshot" />
                <RoleRow code="139" label="Returned Quantity" verified="partial" note="role 24 = role 23 + role 139 in every sample found (an addition, not a shortfall) - semantically inconsistent with 'Fulfilled < Notified'" />
              </tbody>
            </table>
          </div>
          <div className="flex items-start gap-3 bg-[#FFFAEB] border border-[#FEDF89] rounded-lg p-4 mt-4" data-testid="unresolved-banner">
            <Warning size={20} className="text-[#B54708] shrink-0 mt-0.5" weight="fill" />
            <p className="text-sm text-[#B54708] leading-relaxed">
              Working theory: SAP's UI likely computes "Fulfilled Quantity" dynamically from Put-Away/GR confirmation
              history rather than storing it as a queryable field on either custom service. Never proven directly
              (no access to SAP's own UI debug/technical view) - if a future investigation finds the real field,
              update this page.
            </p>
          </div>
        </Section>

        <Section number={5} title="Decision taken" testId="section-decision">
          <p>
            Per explicit user decision, the "Fulfilled" side of the new admin flag uses <strong>this app's own
            Put-Away/GR confirmation tracking</strong> (<code className="bg-[#F2F4F7] px-1 rounded text-xs">put_away_confirmed</code> on
            each shipment's <code className="bg-[#F2F4F7] px-1 rounded text-xs">sap_gr_result.per_po</code> entry) instead of a second SAP read.
            A PO that is <code className="bg-[#F2F4F7] px-1 rounded text-xs">status=="posted"</code> but not yet <code className="bg-[#F2F4F7] px-1 rounded text-xs">put_away_confirmed</code> gets
            flagged, showing SAP's (reliable) Delivery Notification Qty as the at-risk amount. Implemented in
            <code className="bg-[#F2F4F7] px-1 rounded text-xs"> attach_delivery_fulfillment_variance_flag_bulk</code> (<code className="bg-[#F2F4F7] px-1 rounded text-xs">supplier_shipment_service.py</code>),
            rendered as the "Fulfillment Pending in SAP" badge on Confirmed GRNs.
          </p>
        </Section>

        <div className="my-10 border-t border-dashed border-[#D0D5DD]" />

        <div className="mb-8">
          <p className="font-data text-xs font-bold text-[#B42318] uppercase tracking-widest mb-2">Incident record</p>
          <h2 className="font-heading text-2xl font-bold text-[#101828]">Shipment S001330 posted to SAP twice</h2>
        </div>

        <Section number={6} title="Timeline (all times UTC, from SAP's own CreationDateTime)" testId="section-timeline">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Clock size={16} className="mt-0.5 shrink-0 text-[#667085]" />
              <p><strong>Oct 17, 2026, 20:53 UTC</strong> &mdash; "Post GRN Merge" button added to the GRN Approval page, alongside the existing "Post GRN in SAP" (per-PO) button. Both clickable from this point.</p>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={16} className="mt-0.5 shrink-0 text-[#667085]" />
              <p><strong>Oct 19, 2026, 06:52 UTC</strong> &mdash; combined/merge path promoted to the only button in the UI; the old per-PO button removed from the page.</p>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={16} className="mt-0.5 shrink-0 text-[#B42318]" />
              <p><strong>Oct 21, 2026, 04:35:57 UTC</strong> &mdash; shipment S001330 posted via the merge path &rarr; notification <code className="bg-[#F2F4F7] px-1 rounded text-xs">421-S001330</code> &rarr; SAP Inbound Delivery <strong>56951</strong> (still "In Process" / "Not Released" today).</p>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={16} className="mt-0.5 shrink-0 text-[#B42318]" />
              <p><strong>Oct 21, 2026, 05:00:31 UTC</strong> (+24.6 min) &mdash; the SAME shipment posted again via the per-PO path (likely a stale browser tab still running the pre-Oct-19 JS bundle, 2 days after the button was removed from the UI) &rarr; 6 separate notifications <code className="bg-[#F2F4F7] px-1 rounded text-xs">421-S001330-30603</code>, <code className="bg-[#F2F4F7] px-1 rounded text-xs">-30349</code>, <code className="bg-[#F2F4F7] px-1 rounded text-xs">-30028</code>, <code className="bg-[#F2F4F7] px-1 rounded text-xs">-30158</code>, <code className="bg-[#F2F4F7] px-1 rounded text-xs">-29093</code>, <code className="bg-[#F2F4F7] px-1 rounded text-xs">-30348</code> &rarr; SAP deliveries <strong>56818, 56819, 56820, 56971, 56972, 56973</strong>, all "Finished"/"Released".</p>
            </div>
          </div>
        </Section>

        <Section number={7} title="Root cause" testId="section-root-cause">
          <p>
            No guard existed to stop the SAME shipment being approved via two DIFFERENT code paths
            (<code className="bg-[#F2F4F7] px-1 rounded text-xs">sap_playwright_supplier_pgr_service.py</code> for the old per-PO flow,
            <code className="bg-[#F2F4F7] px-1 rounded text-xs">sap_combined_grn_service.py</code> for the new merge flow). The only existing
            de-duplication (<code className="bg-[#F2F4F7] px-1 rounded text-xs">_recent_po_grn_conflict</code>) only blocked a DIFFERENT shipment
            reusing the same PO within 60 seconds - it had no concept of "this exact shipment was already fully posted,
            25 minutes ago, via a different button."
          </p>
        </Section>

        <Section number={8} title="Fixes in place" testId="section-fixes">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>UI:</strong> old per-PO button fully removed (Oct 19 commit) - only "Post GRN Merge" is clickable now.</li>
            <li><strong>Guard:</strong> <code className="bg-[#F2F4F7] px-1 rounded text-xs">_shipment_already_posted_po_conflict()</code> in <code className="bg-[#F2F4F7] px-1 rounded text-xs">server.py</code> checks, before either approval endpoint runs, whether any PO on the shipment already has <code className="bg-[#F2F4F7] px-1 rounded text-xs">status=="posted"</code> + an <code className="bg-[#F2F4F7] px-1 rounded text-xs">inbound_delivery_id</code> in <code className="bg-[#F2F4F7] px-1 rounded text-xs">sap_gr_result.per_po</code>, and rejects with HTTP 409 if so.</li>
            <li><strong>Combined notification design:</strong> <code className="bg-[#F2F4F7] px-1 rounded text-xs">build_combined_notification_id()</code> keys purely on the shipment's own code (not PO number), so a shipment spanning any number of POs always produces exactly one SAP notification.</li>
            <li><strong>Race-condition hardening (added during this investigation):</strong> <code className="bg-[#F2F4F7] px-1 rounded text-xs">prepare_approval()</code>'s final status-flip write was a plain <code className="bg-[#F2F4F7] px-1 rounded text-xs">{'update_one({"_id": ...})'}</code> with no status filter - two near-simultaneous approval attempts (double-click past the frontend's busy-disable, or two tabs) could both pass the earlier read-only status check before either had written anything. Fixed to an atomic conditional write (<code className="bg-[#F2F4F7] px-1 rounded text-xs">{'update_one({"_id": ..., "status": doc["status"]}, ...)'}</code>, checking <code className="bg-[#F2F4F7] px-1 rounded text-xs">matched_count</code>) so only the first of any such race can proceed; verified directly against MongoDB that a simulated race now always leaves exactly one winner.</li>
          </ul>
        </Section>

        <div className="flex items-start gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4" data-testid="cleanup-banner">
          <Info size={20} className="text-[#1E40AF] shrink-0 mt-0.5" weight="fill" />
          <p className="text-sm text-[#1E40AF] leading-relaxed">
            SAP Inbound Delivery <strong>56951</strong> (the stray merge attempt) is still sitting "In Process" /
            "Not Released" and was not cleaned up as part of this investigation - it needs a manual decision in SAP
            (cancel vs leave) since the goods it describes were already received via the 6 per-PO deliveries.
          </p>
        </div>

        <div className="mt-12 border-t border-[#EAECF0] pt-6 text-center">
          <p className="text-xs text-[#94A3B8] font-data">SAP Integration Reference &middot; Inbound Delivery data model + GRN double-post incident</p>
        </div>
      </main>
    </div>
  );
}
